// Setup › Metrics › Targets (coverage rows 45–48): the company's target rules per metric and
// subject (the company, or one seat), with "New target", edit (value, range, window) and delete
// (AlertDialog). Values are typed the way the numbers form takes them (currency in major units,
// percent in %) and stored as the API's decimals.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TargetIcon } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { MetricDef, Seat, TargetRule } from "../../api/types";
import { isoDate } from "../../focus/weeks";
import { formatMetricValue } from "../format";
import { keys, targetsQuery } from "../queries";
import { Loading } from "../screen-parts";
import { labelOf, PERIOD_OPTIONS, toInputValue, toStoredValue, UNIT_HINT } from "./labels";
import { ConfirmButton, ErrorNotice, FormError, MoreRows } from "./parts";

export function TargetsView({
  api,
  co,
  metrics,
  seats,
  currency,
  readOnly,
  creating,
  onCreating,
}: {
  api: Api;
  co: string;
  metrics: MetricDef[];
  seats: Seat[];
  currency: string;
  readOnly: boolean;
  creating: boolean;
  onCreating: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const q = useInfiniteQuery(targetsQuery(api, co));
  // Every page: the API lists oldest first, so a new target lands on the last page.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = q;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const [editing, setEditing] = useState<TargetRule | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.client.targets.delete({ params: { co, id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.targets(co) }),
  });
  const metricOf = (id: string) => metrics.find((m) => m.id === id);
  const subjectOf = (t: TargetRule) =>
    t.subject_kind === "company"
      ? "Company"
      : (seats.find((s) => s.id === t.subject_id)?.title ?? `Seat …${t.subject_id.slice(-4)}`);
  const value = (t: TargetRule) => {
    const unit = metricOf(t.metric_def_id)?.unit ?? "number";
    const f = (v: string) => formatMetricValue(Number(v), unit, currency);
    return t.value_max === null ? f(t.value) : `${f(t.value)} – ${f(t.value_max)}`;
  };
  if (q.isPending) return <Loading label="Loading targets" rows={4} />;
  if (q.isError) return <ErrorNotice what="Targets" error={q.error} />;
  const rows = q.data.pages.flatMap((p) => p.data);
  return (
    <div className="flex min-w-0 flex-col">
      {!rows.length ? (
        <EmptyState
          variant="card"
          icon={TargetIcon}
          title="No targets yet"
          actions={
            readOnly ? undefined : <Button onClick={() => onCreating(true)}>New target</Button>
          }
        />
      ) : (
        <>
          <Table data-slot="setup-targets">
            <TableCaption className="sr-only">Target rules, per metric and subject.</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Metric</TableHead>
                <TableHead scope="col">Applies to</TableHead>
                <TableHead scope="col">Period</TableHead>
                <TableHead scope="col" numeric>
                  Target
                </TableHead>
                <TableHead scope="col">In effect</TableHead>
                {readOnly ? null : (
                  <TableHead scope="col">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => {
                const name =
                  metricOf(t.metric_def_id)?.name ?? `Metric …${t.metric_def_id.slice(-4)}`;
                return (
                  <TableRow key={t.id} data-target={t.metric_def_id} data-target-id={t.id}>
                    <TableHead scope="row">{name}</TableHead>
                    <TableCell>{subjectOf(t)}</TableCell>
                    <TableCell>{labelOf(PERIOD_OPTIONS, t.period_type as never)}</TableCell>
                    <TableCell numeric>{value(t)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {t.effective_from} – {t.effective_to ?? "open"}
                    </TableCell>
                    {readOnly ? null : (
                      <TableCell className="whitespace-nowrap text-right">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>
                          Edit<span className="sr-only"> target for {name}</span>
                        </Button>
                        <ConfirmButton
                          label="Delete"
                          srLabel={`target for ${name}`}
                          variant="ghost"
                          title={`Delete the ${name} target?`}
                          description={`${subjectOf(t)}, ${value(t)} from ${t.effective_from}. Scorecards from now on use the next rule that applies, or none. Published scorecards keep the target they were scored with.`}
                          confirmLabel="Delete target"
                          pending={remove.isPending}
                          onConfirm={() => remove.mutateAsync(t.id)}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <MoreRows what="targets" q={q} />
        </>
      )}
      {creating ? (
        <TargetDialog
          api={api}
          co={co}
          metrics={metrics.filter((m) => !m.archived_at)}
          seats={seats.filter((s) => s.status !== "closed")}
          currency={currency}
          onClose={() => onCreating(false)}
        />
      ) : null}
      {editing ? (
        <TargetDialog
          api={api}
          co={co}
          target={editing}
          metrics={metrics}
          seats={seats}
          currency={currency}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function TargetDialog({
  api,
  co,
  target,
  metrics,
  seats,
  currency,
  onClose,
}: {
  api: Api;
  co: string;
  target?: TargetRule;
  metrics: MetricDef[];
  seats: Seat[];
  currency: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const editing = target !== undefined;
  const [metricId, setMetricId] = useState(target?.metric_def_id ?? metrics[0]?.id ?? "");
  const metric = metrics.find((m) => m.id === metricId);
  const unit = metric?.unit ?? "number";
  const [subject, setSubject] = useState(
    target ? (target.subject_kind === "company" ? "company" : target.subject_id) : "company",
  );
  const [period, setPeriod] = useState<string>(target?.period_type ?? "week");
  const [value, setValue] = useState(target ? toInputValue(target.value, unit, currency) : "");
  const [max, setMax] = useState(
    target?.value_max ? toInputValue(target.value_max, unit, currency) : "",
  );
  const [from, setFrom] = useState(target?.effective_from ?? isoDate(new Date()));
  const [to, setTo] = useState(target?.effective_to ?? "");
  const stored = toStoredValue(value, unit, currency);
  const storedMax = max.trim() ? toStoredValue(max, unit, currency) : null;
  const range = metric?.direction === "between";
  const windowOk = !to || to >= from;
  const ready = !!metric && stored !== null && (!range || storedMax !== null) && !!from && windowOk;
  const save = useMutation({
    mutationFn: () => {
      const common = {
        value: String(stored),
        value_max: range && storedMax !== null ? String(storedMax) : null,
        effective_from: from,
        effective_to: to || null,
      };
      return target
        ? api.client.targets.update({ params: { co, id: target.id }, body: common })
        : api.client.targets.create({
            params: { co },
            body: {
              ...common,
              metric_def_id: metricId,
              subject_kind: subject === "company" ? "company" : "seat",
              subject_id: subject === "company" ? co : subject,
              period_type: period as TargetRule["period_type"],
            },
          });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.targets(co) });
      // Scorecards read the rules: every cached card of the company is stale now.
      void qc.invalidateQueries({ queryKey: ["company", co] });
      onClose();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  const hint = UNIT_HINT[unit];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-5"
          aria-labelledby="target-dialog-title"
        >
          <DialogHeader>
            <DialogTitle id="target-dialog-title">
              {editing ? `Edit the ${metric?.name ?? "metric"} target` : "New target"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "The metric, subject and period of a rule stay; change its value or when it applies."
                : "What good looks like for one metric, for the company or one seat, from a date on."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="target-metric">Metric</FieldLabel>
              <NativeSelect
                id="target-metric"
                value={metricId}
                disabled={editing}
                onChange={(e) => setMetricId(e.target.value)}
              >
                {metrics.map((m) => (
                  <NativeSelectOption key={m.id} value={m.id}>
                    {m.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor="target-subject">Applies to</FieldLabel>
              <NativeSelect
                id="target-subject"
                value={subject}
                disabled={editing}
                onChange={(e) => setSubject(e.target.value)}
              >
                <NativeSelectOption value="company">The company</NativeSelectOption>
                {seats.map((s) => (
                  <NativeSelectOption key={s.id} value={s.id}>
                    Seat: {s.title}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor="target-period">Period</FieldLabel>
              <NativeSelect
                id="target-period"
                value={period}
                disabled={editing}
                onChange={(e) => setPeriod(e.target.value)}
              >
                {PERIOD_OPTIONS.map((o) => (
                  <NativeSelectOption key={o.value} value={o.value}>
                    {o.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field data-invalid={value && stored === null ? true : undefined}>
              <FieldLabel htmlFor="target-value">{range ? "From" : "Target"}</FieldLabel>
              <Input
                id="target-value"
                inputMode="decimal"
                required
                value={value}
                aria-describedby={hint ? "target-value-hint" : undefined}
                onChange={(e) => setValue(e.target.value)}
              />
              {hint ? <FieldDescription id="target-value-hint">{hint}</FieldDescription> : null}
            </Field>
            {range ? (
              <Field data-invalid={max && storedMax === null ? true : undefined}>
                <FieldLabel htmlFor="target-max">To</FieldLabel>
                <Input
                  id="target-max"
                  inputMode="decimal"
                  required
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                />
              </Field>
            ) : null}
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="target-from">In effect from</FieldLabel>
              <Input
                id="target-from"
                type="date"
                required
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field data-invalid={windowOk ? undefined : true}>
              <FieldLabel htmlFor="target-to">Until (optional)</FieldLabel>
              <Input
                id="target-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
              {windowOk ? null : <FieldDescription>Ends before it starts.</FieldDescription>}
            </Field>
          </div>
          {metric?.archived_at ? <Badge variant="warning">The metric is archived</Badge> : null}
          <FormError
            what={editing ? "Saving the target" : "Creating the target"}
            error={save.error}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="lg" disabled={!ready || save.isPending}>
              {save.isPending ? "Saving…" : editing ? "Save target" : "Create target"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
